import React, { PureComponent } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { Button } from '@nearpod-packages/base-library'
import { IGameQuestion, IGameAnswer, AnswerType } from '../../helpers/data/question'

import minQueryHelper from '../../helpers/minQueryHelper'
import AudioElement from '../../components/audio/AudioElement'
import getPalette, { IPalette } from '../../helpers/colors/colors'
import { IAssetData } from '../../helpers/data/assetData'
import Particles from '../../components/particles/Particles'
import getFont from '../../helpers/fonts/fonts'
import answerStates from '../../helpers/enums/answerStates'
import Answer, { IAnswerSkin } from './components/Answer'
import { ReactComponent as Hint } from '../../assets/icons/hint.svg'
import { IAccessibilityStore } from '../../redux/reducers/accessibilityReducer'
import RichTextContent from '../../components/richText/RichTextContent'
import { ensurePlainText } from '../../helpers/html/sanitizer'

import expandIcon from '../../assets/icons/expand-icon.svg'

const palette: IPalette = getPalette()

const fadeIn = keyframes`
	0% {
		opacity: 0;
		animation-timing-function: ease-out;
	}
		100% {
		opacity: 1;
	}
`

const wobbleHorBottom = keyframes`
	0% {
		animation-timing-function: ease-out;
	}
	100% {
		transform: translateX(0%);
		transform-origin: 50% 50%;
	}
	15% {
		transform: translateX(-30px) rotate(-6deg);
	}
	30% {
		transform: translateX(15px) rotate(6deg);
	}
	45% {
		transform: translateX(-15px) rotate(-3.6deg);
	}
	60% {
		transform: translateX(9px) rotate(2.4deg);
	}
	75% {
		transform: translateX(-6px) rotate(-1.2deg);
	}
`

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	background-color: rgb(230, 246, 255);
	overflow: hidden;
	position: relative;
`

const ActivityContainer = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
`

const QuestionContainer = styled.div`
	height: 30%;
	overflow: auto;
	padding: 20px 20px 0;
	text-align: center;
	width: calc(100% - 100px);
	font-size: 12px;

	@media (min-width: 700px) {
		width: 100%;
		font-size: 16px;
	}

	@media (min-width: 991px) {
		height: 28%;
		margin-bottom: 16px;
	}
`

const ParticlesContainer = styled.div`
	width: 100%;
	height: 100%;
	animation: ${fadeIn} 1s ease-out 1s;
	position: absolute;
	top: 0;
	left: 0;
`

const QuestionImage = styled.img`
	max-width: 230px;
	max-height: 185px;
	object-fit: contain;

	&.with-text {
		max-width: 140px;
		max-height: 105px;
		float: left;
	}

	@media (max-width: ${minQueryHelper}px) {
		&:not(.with-text) {
			max-width: 200px;
			max-height: 100px;
			margin: 0 auto;
			position: relative;
			right: -50px;
		}

		&.with-text {
			max-width: 120px;
		}
	}
`
const IconContainer = styled.div`
	padding-right: 20px;
	padding-left: 20px;
	position: relative;
	max-width: 100%;
	max-height: 100%;
	float: left;
	margin-right: 10px;
	border: 1px solid ${palette.darkBlue};
	background-color: ${palette.darkBlue};

	&.clickable {
		cursor: pointer;
	}
`

const Icon = styled.img`
	position: absolute;
	bottom: 0;
	right: 0;
`

const QuestionText = styled.h3`
	color: ${palette.grayBlue};
	font-family: ${getFont('secondaryRegular')};
	font-size: 22px;
	font-weight: normal;
	text-align: center;
	white-space: pre-wrap;
	margin: 0;

	&.with-image {
		text-align: left;
	}

	@media (max-width: ${minQueryHelper}px) {
		font-size: 18px;
		text-align: left;
	}
`

const AnswersContainer = styled.div<{ $hasSubmitButton?: boolean; $isMultipleAnswer?: boolean }>`
	width: 100%;
	height: 70%;
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	overflow: auto;
	padding: 15px 7.5px;
	text-align: center;

	${({ $hasSubmitButton, $isMultipleAnswer }) => $hasSubmitButton && css`
		@media (max-width: ${minQueryHelper}px) {
			height: ${$isMultipleAnswer ? '59%' : '62%'};
		}
	`}
`

const HintBox = styled.div`
	display: block;
	background-color: ${palette.yellowDark};
	font-family: ${getFont('primaryMedium')};
	padding: 8px;
	margin-left: 60px;
	text-align: center;
	max-width: 164px;
	font-size: 14px;
	position: absolute;
	top: -73px;
	animation: ${fadeIn} 0.5s ease-out, ${wobbleHorBottom} 0.8s both 1.5s;

	&:after {
		content: ' ';
		position: absolute;
		top: 100%;
		left: 50%;
		margin-left: -5px;
		border-width: 5px;
		border-style: solid;
		border-color: ${palette.yellowDark} transparent transparent transparent;
	}
`

const HintBoxTitle = styled.div`
	display: flex;
	justify-content: center;
`

const DescriptionText = styled.p`
	padding: 0 15px;
	font-family: ${getFont('secondaryRegular')};
	color: ${palette.grayBlue};
	position: relative;
	top: 10px;
	font-size: 22px;

	@media (min-width: 991px) {
		font-size: 24px;
		position: absolute;
		top: -8px;
	}
`

const ScreenReaderAnnouncement = styled.div`
	position: absolute;
	left: -9999px;
	width: 1px;
	height: 1px;
	overflow: hidden;
`

const AnswerContainer = styled.div`
	width: 50%;
	height: 49.5%;
	max-height: 49.5%;
	min-height: 100px;
	display: inline-block;
	vertical-align: top;
	padding: 7.5px;
	&.small {
		width: 25%;
	}
`

const SubmitButtonContainer = styled.div`
	background-color: ${palette.white};
	display: none;

	@media (max-width: ${minQueryHelper}px) {
		display: block;
		padding: 12px;
	}
`

interface IElementsSkin {
	primary: string
	secondary: string
	particle: IAssetData
	particleSystem: string
}

export interface ISkin {
	elements: IElementsSkin[]
	answers: IAnswerSkin[]
	particleSystems: any
}

interface GameSize {
	width: number
	height: number
}

interface IQuestionProps {
	data: IGameQuestion
	isAnswered: boolean
	isFinished: boolean
	isPreview: boolean
	onSelect?: (id: string) => void
	questionIndex: number
	skin: ISkin
	gameSize: GameSize
	onQuestionImageSelect?: () => void
	selectedAnswersIDs: string[] | null
	isMultipleAnswer: boolean
	isCorrect: boolean
	accessibility?: IAccessibilityStore
	areAnimationsEnabled?: boolean
	shouldShowSubmitButton?: boolean
	submitButtonAriaLabel?: string
	onManualSubmit?: () => void
	handleNextQuestionKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void
}

interface IQuestionState {
	isHintVisible: boolean
	// Screen-reader text per answer uid, as reported by each Answer once MathJax settles. Empty for
	// answers that have not typeset yet; getAccessibilityMessage falls back to plain text.
	answerSpeech: { [answerUid: string]: string }
}

class Question extends PureComponent<IQuestionProps, IQuestionState> {
	constructor(props: IQuestionProps) {
		super(props)
		this.state = {
			isHintVisible: false,
			answerSpeech: {},
		}
	}

	static defaultProps = {
		isAnswered: false,
		isFinished: false,
		isPreview: false,
		selectedAnswersIDs: null,
		isMultipleAnswer: false,
		isCorrect: false,
		accessibility: {},
	}


	hintBoxSetTimeoutID?: NodeJS.Timeout

	handleClickOutside = () => {
		if (this.state.isHintVisible) {
			this.setState({ isHintVisible: false })
			if (this.hintBoxSetTimeoutID) {
				clearTimeout(this.hintBoxSetTimeoutID)
			}
		}
	}

	componentWillUnmount() {
		if (this.props.isMultipleAnswer) {
			if (this.hintBoxSetTimeoutID) {
				clearTimeout(this.hintBoxSetTimeoutID)
			}
			document.removeEventListener('mousedown', this.handleClickOutside)
		}
	}

	// Every answer is rendered and typeset on screen, correct ones included, so the speech this
	// announcement needs already exists inside Answer. Collecting it here avoids typesetting the same
	// formulas a second time, and it lands long before the question ends, so the live region's first
	// non-empty value is already the right one — no double announcement.
	handleAnswerSpeechChange = (answerUid: string, speech: string) => {
		this.setState(previousState =>
			previousState.answerSpeech[answerUid] === speech
				? null
				: { answerSpeech: { ...previousState.answerSpeech, [answerUid]: speech } },
		)
	}

	getAccessibilityMessage = (): string => {
		if (!this.props.isAnswered || !this.props.isFinished) {
			return ''
		}
		const correctAnswers = this.props.data.answers.filter(answer => answer.correct)
		const answerTexts = correctAnswers.map((answer, index) => {
			if (answer.media.type === AnswerType.TEXT) {
				// This announcement is read by a screen reader, so it needs plain text: MathJax's speech
				// ("one half" rather than \frac{1}{2}) when typesetting has settled, plain text otherwise.
				return this.state.answerSpeech[answer.uid] || ensurePlainText(answer.media.payload.text)
			} else if (answer.media.type === AnswerType.IMAGE) {
				const { accessibility } = this.props
				const accessibilityData = accessibility?.[answer.uid]
				if (accessibilityData?.altText) {
					return accessibilityData.altText
				}
				return `Image answer ${index + 1}`
			}
			return ''
		}).filter(text => text !== '')

		const feedback = this.props.isCorrect ? 'Nice Job.' : 'Aw, snap!'
		const plural = answerTexts.length > 1 ? 'Answers' : 'Answer'
		const answers = answerTexts.join(', ')

		return `${feedback} Correct ${plural}: ${answers}`
	}

	onSelect = (answer: IGameAnswer) => {
		if (!this.props.isAnswered) {
			if (this.props.onSelect) {
				this.props.onSelect(answer.uid)
			}
			this.setHintTimeout()
		}
	}

	setHintTimeout = () => {
		if (this.props.isMultipleAnswer && !this.props.isAnswered) {
			if (this.hintBoxSetTimeoutID) {
				clearTimeout(this.hintBoxSetTimeoutID)
			}
			this.hintBoxSetTimeoutID = setTimeout(() => {
				if (!this.props.isAnswered) {
					document.removeEventListener('mousedown', this.handleClickOutside)
					document.addEventListener('mousedown', this.handleClickOutside)
					this.setState({ isHintVisible: true })
				}
			}, 10000)
		}
	}


	/* build the list of Answer components setting each answerState[ CORRECT || INCORRECT || UNANSWERED || UNSELECTED || SELECTED] based on:
	- Iterating over all the answers of the Question
	- the Question general state (isAnswered, isFinished)
	- the answer the student selected: studentSelectedAnswer
	*/
	buildAnswers(answers: IGameAnswer[], answerColors: IAnswerSkin[]) {
		const lastSelectedAnswerID =
			this.props.selectedAnswersIDs && this.props.selectedAnswersIDs[this.props.selectedAnswersIDs.length - 1]

		const isSomeAnswerSelected = this.props.selectedAnswersIDs

		let headerText = ''

		//get the last answer the student selected
		let studentSelectedAnswer: IGameAnswer | undefined

		if (isSomeAnswerSelected) {
			studentSelectedAnswer = answers.find((answer: IGameAnswer) => answer.uid === lastSelectedAnswerID)
		}

		//? iteration over all the answers, mainly to set the proper answerComponentState
		return answers.map((currentAnswer: IGameAnswer, index: number) => {
			let animationPath = ''
			let delay = 0

			//set answer default state to UNANSWERED
			let answerComponentState = answerStates.UNANSWERED

			//question partially answered (some answers has been selected, but more answers still needed)
			if (isSomeAnswerSelected && !this.props.isAnswered) {
				//Answer SELECTED (by the student)
				if (
					studentSelectedAnswer &&
					this.props.selectedAnswersIDs &&
					this.props.selectedAnswersIDs.includes(currentAnswer.uid)
				) {
					answerComponentState = answerStates.SELECTED
				}
			}

			//question fully answered (the student selected the necessary number of answers)
			if (this.props.isAnswered) {
				//! question NOT finished/ended (another students need to answer and still time pending in the countdown)
				if (!this.props.isFinished) {
					//Answer SELECTED (by the student)
					if (
						studentSelectedAnswer &&
						this.props.selectedAnswersIDs &&
						this.props.selectedAnswersIDs.includes(currentAnswer.uid)
					) {
						answerComponentState = answerStates.SELECTED
					} else {
						//Answer UNSELECTED (this one was not selected)
						answerComponentState = answerStates.UNSELECTED
					}

					//! question finished/ended and the answer was selected by the student (could be CORRECT or INCORRECT)
				} else if (this.props.selectedAnswersIDs && this.props.selectedAnswersIDs.includes(currentAnswer.uid)) {
					//Answer CORRECT (was selected by the student)
					if (currentAnswer.correct) {
						answerComponentState = answerStates.CORRECT
						animationPath = 'assets/views/game/correct.json'
						headerText = 'Nice job!'
					} else {
						//Answer INCORRECT (was selected by the student)
						answerComponentState = answerStates.INCORRECT
						animationPath = 'assets/views/game/incorrect.json'
						headerText = 'Aw, snap!'
					}
					//! question finished/ended and student selected an INCORRECT answer
				} else {
					delay = 1500

					//Answer CORRECT (was NOT selected by the student)
					if (currentAnswer.correct) {
						answerComponentState = answerStates.CORRECT
						animationPath = 'assets/views/game/correct.json'
						headerText = 'Correct Answer'
					} else {
						//Answer UNSELECTED (this is an INCORRECT answer and was not selected by the student)
						answerComponentState = answerStates.UNSELECTED
					}
				}
			}

			const answerSkin: IAnswerSkin = answerColors[index % answerColors.length]

			const gameSize = this.props.gameSize
			const isSmall = currentAnswer.media.type === AnswerType.IMAGE && gameSize.height < 500
			const className = isSmall ? 'small' : ''

			return (
				<AnswerContainer
					className={className}
					key={currentAnswer.uid}
				>
					<Answer
						answerData={currentAnswer}
						delay={delay}
						headerText={headerText}
						animationPath={animationPath}
						answerSkin={answerSkin}
						answerState={answerComponentState}
						onSelect={this.onSelect}
						isMultipleAnswer={this.props.isMultipleAnswer}
						accessibility={this.props.accessibility}
						onSpeechChange={this.handleAnswerSpeechChange}
					/>
				</AnswerContainer>
			)
		})
	}

	buildAudio() {
		let audioSrc
		if (this.props.isAnswered && this.props.isFinished) {
			if (this.props.isCorrect) {
				audioSrc = 'audios/correct.mp3'
			} else {
				audioSrc = 'audios/incorrect.mp3'
			}
		}
		return audioSrc
	}

	render() {
		const elementsData = this.props.skin.elements
		const elementData = elementsData[this.props.questionIndex % elementsData.length]
		const particleSystem = this.props.skin.particleSystems
			? this.props.skin.particleSystems[elementData.particleSystem]
			: null
		const audio = this.buildAudio()
		const accessibilityMessage = this.getAccessibilityMessage()

		const questionImageClassNames: string[] = []
		const iconContainerClassNames: string[] = []
		if (!!this.props.data.text) {
			questionImageClassNames.push('with-text')
		}
		if (!this.props.isPreview) {
			iconContainerClassNames.push('clickable')
		}

		return (
			<Wrapper style={{ backgroundColor: elementData.secondary }}>
				{this.props.areAnimationsEnabled && particleSystem && (
					<ParticlesContainer>
						<Particles particle={elementData.particle.path} particleSystem={particleSystem} />
					</ParticlesContainer>
				)}
				<main role="main" aria-label="Quiz Question">
					<article>
						<ActivityContainer>
							<QuestionContainer role="region" aria-labelledby="question-text">
								{this.props.data.asset && (
									<>
										<IconContainer onClick={this.props.onQuestionImageSelect} className={iconContainerClassNames.join(' ')}>
											<QuestionImage
												src={this.props.data.asset.source}
												alt={!this.props.data.asset.isDecorative ? this.props.data.asset.altText || 'Reference Image' : ''}
												aria-hidden={this.props.data.asset.isDecorative}
												className={questionImageClassNames.join(' ')}
											/>
											{!this.props.isPreview && <Icon src={expandIcon} />}
										</IconContainer>
									</>
								)}
								{this.props.data.text && (
									<RichTextContent
										as={QuestionText}
										id="question-text"
										role="heading"
										className={!!this.props.data.asset ? 'with-image' : ''}
										content={this.props.data.text}
										// Both aria-labelledby (question region) and aria-describedby (answers group)
										// resolve through this element, and post-typeset its text content is glyphs.
										speechOutlet="label"
										// Nothing encloses the question text, so the formula owns its own interaction:
										// clickable and keyboard-explorable via MathJax's Explorer.
										isInteractive
									/>
								)}
							</QuestionContainer>

							{this.props.isMultipleAnswer && (
								<div style={{ position: 'relative' }}>
									{this.state.isHintVisible && (
										<HintBox role="alert">
											<HintBoxTitle>
												<h4 style={{ marginRight: '2px' }}>HINT</h4> <Hint />{' '}
											</HintBoxTitle>
											There is more than one correct answer
										</HintBox>
									)}

									<DescriptionText>Select all answers that apply</DescriptionText>
								</div>
							)}

							<AnswersContainer
								$hasSubmitButton={this.props.shouldShowSubmitButton}
								$isMultipleAnswer={this.props.isMultipleAnswer}
								role={this.props.isMultipleAnswer ? "group" : "radiogroup"}
								aria-label="Answer choices"
								aria-describedby="question-text">
								{this.buildAnswers(this.props.data.answers, this.props.skin.answers)}
							</AnswersContainer>

							{this.props.shouldShowSubmitButton && (
								<SubmitButtonContainer>
									<Button
										style={{ width: '100%' }}
										variant="primary" 
										rightIcon="check" 
										disabled={this.props.isAnswered}
										aria-label={this.props.submitButtonAriaLabel}
										onClick={this.props.onManualSubmit} 
										onKeyDown={this.props.handleNextQuestionKeyDown} 
									>
										Submit answer
									</Button>
								</SubmitButtonContainer>
							)}
						</ActivityContainer>
					</article>
				</main>
				{audio && <AudioElement loop={false} src={audio} />}
				<ScreenReaderAnnouncement
					role="alert"
					aria-live="assertive"
					aria-atomic="true"
				>
					{accessibilityMessage}
				</ScreenReaderAnnouncement>
			</Wrapper>
		)
	}
}

export default Question
